import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { hasAnyRole, M16_ROLES } from '../../lib/roles';
import type { InspectionStatus, InspectionType, WorkplaceInspection } from '../../lib/types';
import { useCursorList } from '../../lib/useCursorList';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Pagination, Spinner, StatusBadge, Tabs } from '../../ui/components';

const TYPES: InspectionType[] = ['ROUTINE', 'COMPLAINT', 'OSH'];
const STATUSES: InspectionStatus[] = ['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];

const TAB_LABEL = { queue: 'Queue', schedule: 'Schedule new' } as const;
type TabKey = keyof typeof TAB_LABEL;

export function InspectionsHome() {
  const { persona } = useAuth();
  const canSchedule = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_OFFICER, M16_ROLES.LABOUR_INSPECTOR, M16_ROLES.CHIEF_INSPECTOR);
  const [tab, setTab] = useState<TabKey>('queue');

  return (
    <div>
      <PageHeader title="Workplace Inspections" subtitle="Routine, complaint-triggered and OSH inspections, compliance notices and follow-ups." />
      <Tabs
        tabs={(canSchedule ? (['queue', 'schedule'] as TabKey[]) : (['queue'] as TabKey[])).map((k) => ({ key: k, label: TAB_LABEL[k] }))}
        value={tab}
        onChange={setTab}
      />
      {tab === 'queue' ? <QueueTab /> : null}
      {tab === 'schedule' ? <ScheduleTab /> : null}
    </div>
  );
}

function QueueTab() {
  const { persona } = useAuth();
  const isInspector = hasAnyRole(persona?.roles, M16_ROLES.LABOUR_INSPECTOR);
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [employerEntityId, setEmployerEntityId] = useState('');
  const [q, setQ] = useState('');
  const [mine, setMine] = useState(isInspector);

  const query = new URLSearchParams();
  if (type) query.set('type', type);
  if (status) query.set('status', status);
  if (employerEntityId) query.set('employerEntityId', employerEntityId);
  if (q) query.set('q', q);
  if (mine) query.set('inspectorId', 'me');
  const path = `/api/v1/m16/inspections${query.toString() ? `?${query.toString()}` : ''}`;
  const listQ = useCursorList<WorkplaceInspection>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Type" className="!mb-0">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Employer entity ID" className="!mb-0">
          <input className="field-input" value={employerEntityId} onChange={(e) => setEmployerEntityId(e.target.value)} placeholder="BIZ-SC-…" />
        </Field>
        <Field label="Search" className="!mb-0">
          <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ref no or district…" />
        </Field>
        {isInspector ? (
          <label className="flex items-center gap-2 pb-2 text-sm font-medium text-slate-600">
            <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />
            My queue only
          </label>
        ) : null}
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : listQ.items.length === 0 ? (
          <EmptyState title="No inspections match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Type</th>
                <th className="table-head">Employer</th>
                <th className="table-head">District</th>
                <th className="table-head">Scheduled</th>
                <th className="table-head">Inspector</th>
                <th className="table-head">Status</th>
                <th className="table-head">Compliance</th>
              </tr>
            </thead>
            <tbody>
              {listQ.items.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">
                    <Link className="font-semibold text-brand-600 hover:underline" to={`/inspections/${i.id}`}>
                      {i.refNo}
                    </Link>
                  </td>
                  <td className="table-cell text-xs">{i.type}</td>
                  <td className="table-cell font-mono text-xs text-slate-500">{i.employerEntityId}</td>
                  <td className="table-cell text-xs">{i.district ?? '—'}</td>
                  <td className="table-cell text-xs">{i.scheduledAt ? formatDate(i.scheduledAt) : '—'}</td>
                  <td className="table-cell font-mono text-xs text-slate-500">{i.inspectorId ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={i.status} />
                  </td>
                  <td className="table-cell">{i.complianceStatus ? <StatusBadge status={i.complianceStatus} /> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <Pagination hasMore={listQ.hasMore} onMore={listQ.loadMore} loading={listQ.loadingMore} />
      </div>
    </div>
  );
}

function ScheduleTab() {
  const [type, setType] = useState<InspectionType>('ROUTINE');
  const [employerEntityId, setEmployerEntityId] = useState('');
  const [relatedGrievanceId, setRelatedGrievanceId] = useState('');
  const [district, setDistrict] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [inspectorId, setInspectorId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<WorkplaceInspection | null>(null);

  const submit = async () => {
    if (type === 'COMPLAINT' && !relatedGrievanceId.trim()) return;
    if (type !== 'COMPLAINT' && !employerEntityId.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { type };
      if (employerEntityId.trim()) body.employerEntityId = employerEntityId.trim();
      if (type === 'COMPLAINT') body.relatedGrievanceId = relatedGrievanceId.trim();
      if (district.trim()) body.district = district.trim();
      if (scheduledAt) body.scheduledAt = new Date(scheduledAt).toISOString();
      if (inspectorId.trim()) body.inspectorId = inspectorId.trim();
      const res = await api.post<WorkplaceInspection>('/api/v1/m16/inspections', body);
      setCreated(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Scheduling failed');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className="card max-w-xl border-emerald-200 bg-emerald-50 p-5">
        <h2 className="text-sm font-bold text-emerald-800">Inspection scheduled — {created.refNo}</h2>
        <div className="mt-3 flex gap-2">
          <Link to={`/inspections/${created.id}`} className="btn-primary">
            View inspection
          </Link>
          <button className="btn-secondary !bg-white" onClick={() => setCreated(null)}>
            Schedule another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card max-w-xl p-5">
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="Type">
          <select className="field-input" value={type} onChange={(e) => setType(e.target.value as InspectionType)}>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        {type === 'COMPLAINT' ? (
          <Field label="Related grievance ID" hint="Employer is taken from this grievance automatically.">
            <input className="field-input" value={relatedGrievanceId} onChange={(e) => setRelatedGrievanceId(e.target.value)} />
          </Field>
        ) : null}
        <Field label={type === 'COMPLAINT' ? 'Employer entity ID (optional — filled from the grievance)' : 'Employer entity ID'}>
          <input className="field-input" value={employerEntityId} onChange={(e) => setEmployerEntityId(e.target.value)} placeholder="e.g. BIZ-SC-200102" />
        </Field>
        <Field label="District (optional)">
          <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="e.g. Anse Royale" />
        </Field>
        <Field label="Scheduled at (optional)">
          <input type="datetime-local" className="field-input" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
        </Field>
        <Field label="Inspector ID (optional)" hint="Leave blank to self-assign if you are a Labour Inspector.">
          <input className="field-input" value={inspectorId} onChange={(e) => setInspectorId(e.target.value)} placeholder="e.g. inspector-d-athanase" />
        </Field>
        {type === 'COMPLAINT' ? <InfoNote message="A COMPLAINT inspection requires the related grievance to already exist." /> : null}
        <button
          className="btn-primary w-full"
          onClick={submit}
          disabled={busy || (type === 'COMPLAINT' ? !relatedGrievanceId.trim() : !employerEntityId.trim())}
        >
          {busy ? 'Scheduling…' : 'Schedule inspection'}
        </button>
      </div>
    </div>
  );
}
